import { useState, useEffect, useMemo, useCallback } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Users, Loader2, CheckCircle2, XCircle } from 'lucide-react';

const ASSETS = [
  { symbol: 'BTC', name: 'Bitcoin' }, { symbol: 'ETH', name: 'Ethereum' }, { symbol: 'BNB', name: 'BNB' },
  { symbol: 'SOL', name: 'Solana' }, { symbol: 'XRP', name: 'XRP' }, { symbol: 'ADA', name: 'Cardano' },
  { symbol: 'DOGE', name: 'Dogecoin' }, { symbol: 'AVAX', name: 'Avalanche' }, { symbol: 'DOT', name: 'Polkadot' },
  { symbol: 'MATIC', name: 'Polygon' }, { symbol: 'LINK', name: 'Chainlink' }, { symbol: 'UNI', name: 'Uniswap' },
  { symbol: 'LTC', name: 'Litecoin' }, { symbol: 'BCH', name: 'Bitcoin Cash' }, { symbol: 'ATOM', name: 'Cosmos' },
  { symbol: 'FIL', name: 'Filecoin' }, { symbol: 'APT', name: 'Aptos' }, { symbol: 'ARB', name: 'Arbitrum' },
  { symbol: 'OP', name: 'Optimism' }, { symbol: 'NEAR', name: 'NEAR' },
  { symbol: 'XAUUSD', name: 'Gold' }, { symbol: 'XAGUSD', name: 'Silver' },
  { symbol: 'USOIL', name: 'Crude Oil' }, { symbol: 'NGAS', name: 'Natural Gas' },
];

const emptyPnl = {
  client_id: '', account_id: '', trader_id: '', date: '',
  symbol: '', direction: 'long', entry_price: '', exit_price: '', quantity: '',
  gross_profit: '', loss: '', notes: ''
};

export default function PnlPage() {
  const [entries, setEntries] = useState([]);
  const [clients, setClients] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [traders, setTraders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPnl);

  // Bulk trade state
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkTrade, setBulkTrade] = useState({
    date: new Date().toISOString().slice(0, 10),
    symbol: 'BTC', asset_name: 'Bitcoin', direction: 'long',
    entry_price: '', exit_price: '', trader_id: '', notes: '',
  });
  const [bulkQty, setBulkQty] = useState({}); // { client_id: '<qty string>' }
  const [bulkSearch, setBulkSearch] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [pRes, cRes, aRes, tRes] = await Promise.all([
        api.get('/pnl'),
        api.get('/clients'),
        api.get('/accounts'),
        api.get('/users', { params: { role: 'trader' } }).catch(() => ({ data: [] }))
      ]);
      setEntries(pRes.data);
      setClients(cRes.data);
      setAccounts(aRes.data);
      setTraders(tRes.data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => {
    setEditing(null);
    setForm({...emptyPnl, date: new Date().toISOString().split('T')[0]});
    setDialogOpen(true);
  };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      client_id: e.client_id, account_id: e.account_id || '', trader_id: e.trader_id || '',
      date: e.date,
      symbol: e.symbol || '', direction: e.direction || 'long',
      entry_price: e.entry_price != null ? String(e.entry_price) : '',
      exit_price: e.exit_price != null ? String(e.exit_price) : '',
      quantity: e.quantity != null ? String(e.quantity) : '',
      gross_profit: String(e.gross_profit), loss: String(e.loss), notes: e.notes || ''
    });
    setDialogOpen(true);
  };

  // Auto-calculated P&L preview
  const calcPreview = useMemo(() => {
    const entry = parseFloat(form.entry_price);
    const exit = parseFloat(form.exit_price);
    const qty = parseFloat(form.quantity);
    if (!entry || !exit || !qty) return null;
    const raw = form.direction === 'long' ? (exit - entry) * qty : (entry - exit) * qty;
    const pct = entry > 0 ? ((form.direction === 'long' ? (exit - entry) : (entry - exit)) / entry) * 100 : 0;
    return { net: Math.round(raw * 100) / 100, pct: Math.round(pct * 100) / 100 };
  }, [form.entry_price, form.exit_price, form.quantity, form.direction]);

  // ─── Bulk trade helpers ───
  const bulkClients = useMemo(() => {
    const s = bulkSearch.trim().toLowerCase();
    let list = clients.filter(c => c.status === 'active' || !c.status);
    if (s) list = list.filter(c => (c.name || '').toLowerCase().includes(s) || (c.client_id || '').toLowerCase().includes(s));
    return list;
  }, [clients, bulkSearch]);

  const bulkPreview = useMemo(() => {
    const entry = parseFloat(bulkTrade.entry_price);
    const exit = parseFloat(bulkTrade.exit_price);
    if (!entry || !exit) return { rowCount: 0, totalQty: 0, totalNet: 0, perUnit: 0 };
    const perUnit = bulkTrade.direction === 'long' ? (exit - entry) : (entry - exit);
    let totalQty = 0, totalNet = 0, rowCount = 0;
    Object.entries(bulkQty).forEach(([, q]) => {
      const n = parseFloat(q); if (!n || n <= 0) return;
      totalQty += n; totalNet += n * perUnit; rowCount += 1;
    });
    return { rowCount, totalQty: Math.round(totalQty * 100) / 100, totalNet: Math.round(totalNet * 100) / 100, perUnit: Math.round(perUnit * 100) / 100 };
  }, [bulkQty, bulkTrade.entry_price, bulkTrade.exit_price, bulkTrade.direction]);

  const openBulk = () => {
    setBulkTrade({
      date: new Date().toISOString().slice(0, 10),
      symbol: 'BTC', asset_name: 'Bitcoin', direction: 'long',
      entry_price: '', exit_price: '', trader_id: '', notes: '',
    });
    setBulkQty({});
    setBulkSearch('');
    setBulkOpen(true);
  };

  const handleBulkSave = async () => {
    const entry = parseFloat(bulkTrade.entry_price);
    const exit = parseFloat(bulkTrade.exit_price);
    if (!bulkTrade.date) { toast.error('Trade date is required'); return; }
    if (!entry || !exit) { toast.error('Entry and Exit price are required'); return; }
    const rows = Object.entries(bulkQty)
      .map(([client_id, q]) => ({ client_id, quantity: parseFloat(q) }))
      .filter(r => r.quantity && r.quantity > 0);
    if (rows.length === 0) { toast.error('Enter quantity for at least one client'); return; }
    setBulkSaving(true);
    try {
      const payload = {
        date: bulkTrade.date, symbol: bulkTrade.symbol, asset_name: bulkTrade.asset_name,
        direction: bulkTrade.direction, entry_price: entry, exit_price: exit,
        trader_id: bulkTrade.trader_id || null, notes: bulkTrade.notes || '',
        entries: rows,
      };
      const { data } = await api.post('/pnl/bulk', payload);
      const netFmt = Number(data.total_net_pnl || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      toast.success(`Bulk trade saved — ${data.count} clients · net $${netFmt}${data.skipped?.length ? ` · ${data.skipped.length} skipped` : ''}`);
      setBulkOpen(false);
      fetchData();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || 'Bulk save failed');
    } finally { setBulkSaving(false); }
  };

  const handleSave = async () => {
    if (!form.client_id || !form.date) {
      toast.error('Client and Date are required');
      return;
    }
    try {
      const asset = ASSETS.find(a => a.symbol === form.symbol);
      const payload = {
        ...form,
        asset_name: asset ? asset.name : undefined,
        entry_price: form.entry_price !== '' ? parseFloat(form.entry_price) : null,
        exit_price: form.exit_price !== '' ? parseFloat(form.exit_price) : null,
        quantity: form.quantity !== '' ? parseFloat(form.quantity) : null,
        gross_profit: parseFloat(form.gross_profit) || 0,
        loss: parseFloat(form.loss) || 0,
      };
      // Clean empty strings to null
      ['account_id','trader_id','symbol','direction'].forEach(k => { if (payload[k] === '') payload[k] = null; });
      if (editing) { await api.put(`/pnl/${editing.id}`, payload); toast.success('Trade updated'); }
      else { await api.post('/pnl', payload); toast.success('Trade recorded'); }
      setDialogOpen(false); fetchData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this trade entry?')) return;
    try { await api.delete(`/pnl/${id}`); toast.success('Entry deleted'); fetchData(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const getClientName = (id) => clients.find(c => c.id === id)?.name || '-';
  const totalProfit = entries.reduce((s, e) => s + (e.gross_profit || 0), 0);
  const totalLoss = entries.reduce((s, e) => s + (e.loss || 0), 0);
  const netPnl = entries.reduce((s, e) => s + (e.net_pnl || 0), 0);
  const fmt = (n, d = 2) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: d });

  return (
    <div className="space-y-6" data-testid="pnl-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Trade Book — P&L</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Daily trade entries with full execution detail (asset, direction, entry/exit, quantity, auto P&L)</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={openBulk} variant="outline" className="rounded-sm" data-testid="bulk-pnl-btn">
            <Users size={16} className="mr-2" /> Bulk Trade
          </Button>
          <Button onClick={openNew} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="add-pnl-btn">
            <Plus size={16} className="mr-2" /> New Trade
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border border-brand-border bg-white">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-10 h-10 rounded-sm bg-brand-success/15 flex items-center justify-center"><TrendingUp size={20} className="text-brand-success" /></div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-text-secondary">Total Profit</p>
              <p className="font-heading text-2xl text-brand-success">${fmt(totalProfit)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-brand-border bg-white">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-10 h-10 rounded-sm bg-brand-error/15 flex items-center justify-center"><TrendingDown size={20} className="text-brand-error" /></div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-text-secondary">Total Loss</p>
              <p className="font-heading text-2xl text-brand-error">${fmt(totalLoss)}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-brand-border bg-white">
          <CardContent className="p-6 flex items-center gap-4">
            <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${netPnl >= 0 ? 'bg-brand-success/15' : 'bg-brand-error/15'}`}>
              {netPnl >= 0 ? <TrendingUp size={20} className="text-brand-success" /> : <TrendingDown size={20} className="text-brand-error" />}
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-text-secondary">Net P&L</p>
              <p className={`font-heading text-2xl ${netPnl >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>${fmt(netPnl)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-brand-border bg-white">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="pnl-table">
              <thead>
                <tr className="border-b border-brand-border bg-brand-bg/50">
                  {['Date', 'Client', 'Asset', 'Side', 'Qty', 'Entry', 'Exit', 'Gross', 'Loss', 'Net P&L', 'Notes', 'By', ''].map(h => (
                    <th key={h} className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider font-semibold text-brand-text-secondary">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan={13} className="py-12 text-center text-brand-text-secondary">Loading...</td></tr> :
                 entries.length === 0 ? <tr><td colSpan={13} className="py-12 text-center text-brand-text-secondary">No trade entries</td></tr> :
                 entries.map(e => (
                  <tr key={e.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                    <td className="py-2.5 px-3 text-brand-text-primary text-xs">{e.date}</td>
                    <td className="py-2.5 px-3 text-brand-text-primary text-xs">{getClientName(e.client_id)}</td>
                    <td className="py-2.5 px-3 text-xs">
                      {e.symbol ? <><span className="font-bold">{e.symbol}</span>{e.asset_name && <span className="text-brand-text-secondary"> · {e.asset_name}</span>}</> : '-'}
                    </td>
                    <td className="py-2.5 px-3">
                      {e.direction ? (
                        <Badge className={`text-[10px] border-0 ${e.direction === 'long' ? 'bg-brand-success/15 text-brand-success' : 'bg-brand-error/15 text-brand-error'}`}>
                          {e.direction === 'long' ? <ArrowUpRight size={10} className="mr-0.5" /> : <ArrowDownRight size={10} className="mr-0.5" />} {e.direction?.toUpperCase()}
                        </Badge>
                      ) : '-'}
                    </td>
                    <td className="py-2.5 px-3 text-xs font-mono">{e.quantity != null ? fmt(e.quantity, 6) : '-'}</td>
                    <td className="py-2.5 px-3 text-xs font-mono">{e.entry_price != null ? `$${fmt(e.entry_price, 6)}` : '-'}</td>
                    <td className="py-2.5 px-3 text-xs font-mono">{e.exit_price != null ? `$${fmt(e.exit_price, 6)}` : '-'}</td>
                    <td className="py-2.5 px-3 text-brand-success font-medium text-xs">${fmt(e.gross_profit)}</td>
                    <td className="py-2.5 px-3 text-brand-error font-medium text-xs">${fmt(e.loss)}</td>
                    <td className={`py-2.5 px-3 font-bold text-xs ${e.net_pnl >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>${fmt(e.net_pnl)}</td>
                    <td className="py-2.5 px-3 text-brand-text-secondary max-w-[140px] truncate text-xs">{e.notes || '-'}</td>
                    <td className="py-2.5 px-3 text-brand-text-secondary text-xs">{e.created_by_name || '-'}</td>
                    <td className="py-2.5 px-3">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(e)} className="h-7 w-7" data-testid={`edit-pnl-${e.id}`}><Pencil size={12} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(e.id)} className="h-7 w-7 text-brand-error" data-testid={`delete-pnl-${e.id}`}><Trash2 size={12} /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pnl-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{editing ? 'Edit Trade Entry' : 'New Trade Entry'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} data-testid="pnl-date-input" className="bg-white border-brand-border rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label>Client *</Label>
              <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="pnl-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Trade details section */}
            <div className="sm:col-span-2 pt-2 border-t border-brand-border">
              <p className="text-xs uppercase tracking-widest text-brand-text-secondary font-semibold mb-2">Trade Details</p>
            </div>
            <div className="space-y-2">
              <Label>Asset / Symbol</Label>
              <Select value={form.symbol} onValueChange={v => setForm({...form, symbol: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="pnl-symbol-select"><SelectValue placeholder="Select asset (BTC, ETH, Gold...)" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {ASSETS.map(a => <SelectItem key={a.symbol} value={a.symbol}>{a.symbol} — {a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Direction</Label>
              <div className="flex gap-2">
                <Button type="button" variant={form.direction === 'long' ? 'default' : 'outline'}
                  onClick={() => setForm({...form, direction: 'long'})}
                  className={`flex-1 rounded-sm h-10 text-xs ${form.direction === 'long' ? 'bg-brand-success hover:bg-brand-success/90 text-white' : 'border-brand-border'}`}
                  data-testid="pnl-long-btn">
                  <ArrowUpRight size={14} className="mr-1" /> LONG (Buy)
                </Button>
                <Button type="button" variant={form.direction === 'short' ? 'default' : 'outline'}
                  onClick={() => setForm({...form, direction: 'short'})}
                  className={`flex-1 rounded-sm h-10 text-xs ${form.direction === 'short' ? 'bg-brand-error hover:bg-brand-error/90 text-white' : 'border-brand-border'}`}
                  data-testid="pnl-short-btn">
                  <ArrowDownRight size={14} className="mr-1" /> SHORT (Sell)
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Entry Price ($)</Label>
              <Input type="number" step="any" value={form.entry_price} onChange={e => setForm({...form, entry_price: e.target.value})} placeholder="0.00" className="bg-white border-brand-border rounded-sm font-mono" data-testid="pnl-entry-input" />
            </div>
            <div className="space-y-2">
              <Label>Exit Price ($)</Label>
              <Input type="number" step="any" value={form.exit_price} onChange={e => setForm({...form, exit_price: e.target.value})} placeholder="0.00" className="bg-white border-brand-border rounded-sm font-mono" data-testid="pnl-exit-input" />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Quantity</Label>
              <Input type="number" step="any" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} placeholder="0.00" className="bg-white border-brand-border rounded-sm font-mono" data-testid="pnl-qty-input" />
            </div>

            {/* Live calc preview */}
            {calcPreview && (
              <div className="sm:col-span-2 p-3 rounded-lg border bg-brand-bg/40 flex items-center justify-between" data-testid="pnl-calc-preview">
                <span className="text-xs text-brand-text-secondary">Auto-calculated P&L:</span>
                <span className={`font-heading font-bold text-xl ${calcPreview.net >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>
                  {calcPreview.net >= 0 ? '+' : ''}${fmt(calcPreview.net)} ({calcPreview.pct >= 0 ? '+' : ''}{calcPreview.pct}%)
                </span>
              </div>
            )}

            <div className="sm:col-span-2 pt-2 border-t border-brand-border">
              <p className="text-xs uppercase tracking-widest text-brand-text-secondary font-semibold mb-2">Manual Override (optional — leave blank to use auto-calc above)</p>
            </div>
            <div className="space-y-2">
              <Label>Gross Profit ($)</Label>
              <Input type="number" value={form.gross_profit} onChange={e => setForm({...form, gross_profit: e.target.value})} placeholder="0.00" data-testid="pnl-profit-input" className="bg-white border-brand-border rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label>Loss ($)</Label>
              <Input type="number" value={form.loss} onChange={e => setForm({...form, loss: e.target.value})} placeholder="0.00" data-testid="pnl-loss-input" className="bg-white border-brand-border rounded-sm" />
            </div>

            <div className="space-y-2">
              <Label>Account (optional)</Label>
              <Select value={form.account_id} onValueChange={v => setForm({...form, account_id: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm"><SelectValue placeholder="Select account" /></SelectTrigger>
                <SelectContent>{accounts.filter(a => !form.client_id || a.client_id === form.client_id).map(a => <SelectItem key={a.id} value={a.id}>{a.broker_name} - {a.trading_id}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Trader</Label>
              <Select value={form.trader_id} onValueChange={v => setForm({...form, trader_id: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="pnl-trader-select"><SelectValue placeholder="Select trader" /></SelectTrigger>
                <SelectContent>{traders.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 space-y-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="pnl-notes-input" placeholder="Optional notes about this trade…" className="bg-white border-brand-border rounded-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-brand-border rounded-sm">Cancel</Button>
            <Button onClick={handleSave} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="pnl-save-btn">{editing ? 'Update' : 'Save Trade'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── BULK TRADE DIALOG ─── */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="p-6 pb-3 border-b border-brand-border">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Users size={18} /> Bulk Trade Entry — Same Trade, Multiple Clients
            </DialogTitle>
            <p className="text-xs text-brand-text-secondary mt-1">Enter the trade once. Fill only the quantity per client — the entry / exit / direction stay the same across everyone.</p>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6 space-y-5">
            {/* Trade meta */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 rounded-lg bg-brand-bg/60 border border-brand-border">
              <div>
                <Label className="text-xs">Date *</Label>
                <Input type="date" value={bulkTrade.date} onChange={e => setBulkTrade({ ...bulkTrade, date: e.target.value })} className="h-9 text-sm" data-testid="bulk-date" />
              </div>
              <div>
                <Label className="text-xs">Asset *</Label>
                <Select value={bulkTrade.symbol} onValueChange={v => { const a = ASSETS.find(x => x.symbol === v); setBulkTrade({ ...bulkTrade, symbol: v, asset_name: a?.name || v }); }}>
                  <SelectTrigger className="h-9 text-sm" data-testid="bulk-symbol"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">{ASSETS.map(a => <SelectItem key={a.symbol} value={a.symbol}>{a.symbol} — {a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Direction *</Label>
                <div className="flex gap-1 h-9">
                  <Button size="sm" type="button" variant={bulkTrade.direction === 'long' ? 'default' : 'outline'} onClick={() => setBulkTrade({ ...bulkTrade, direction: 'long' })} className={`flex-1 rounded-sm ${bulkTrade.direction === 'long' ? 'bg-brand-success text-white hover:bg-brand-success/90' : ''}`} data-testid="bulk-dir-long">Long</Button>
                  <Button size="sm" type="button" variant={bulkTrade.direction === 'short' ? 'default' : 'outline'} onClick={() => setBulkTrade({ ...bulkTrade, direction: 'short' })} className={`flex-1 rounded-sm ${bulkTrade.direction === 'short' ? 'bg-brand-error text-white hover:bg-brand-error/90' : ''}`} data-testid="bulk-dir-short">Short</Button>
                </div>
              </div>
              <div>
                <Label className="text-xs">Trader (optional)</Label>
                <Select value={bulkTrade.trader_id || 'none'} onValueChange={v => setBulkTrade({ ...bulkTrade, trader_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="h-9 text-sm" data-testid="bulk-trader"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {traders.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Entry Price *</Label>
                <Input type="number" step="0.00000001" value={bulkTrade.entry_price} onChange={e => setBulkTrade({ ...bulkTrade, entry_price: e.target.value })} className="h-9 text-sm font-mono" data-testid="bulk-entry-price" />
              </div>
              <div>
                <Label className="text-xs">Exit Price *</Label>
                <Input type="number" step="0.00000001" value={bulkTrade.exit_price} onChange={e => setBulkTrade({ ...bulkTrade, exit_price: e.target.value })} className="h-9 text-sm font-mono" data-testid="bulk-exit-price" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Notes (optional)</Label>
                <Input value={bulkTrade.notes} onChange={e => setBulkTrade({ ...bulkTrade, notes: e.target.value })} className="h-9 text-sm" placeholder="e.g. Fed announcement scalp" />
              </div>
            </div>

            {/* Per-unit preview */}
            <div className="flex items-center flex-wrap gap-3 text-xs">
              <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">Per-unit P&L <span className={`font-mono font-bold ml-1 ${bulkPreview.perUnit >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>${bulkPreview.perUnit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></span>
              <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">Clients selected <span className="font-mono font-bold text-slate-900 ml-1" data-testid="bulk-selected-count">{bulkPreview.rowCount}</span></span>
              <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">Total qty <span className="font-mono font-bold text-slate-900 ml-1">{bulkPreview.totalQty}</span></span>
              <span className={`px-2.5 py-1 rounded-full ${bulkPreview.totalNet >= 0 ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'}`}>Est. total net <span className="font-mono font-bold ml-1" data-testid="bulk-total-net">${bulkPreview.totalNet.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span></span>
            </div>

            {/* Client list with qty per row */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs">Clients — enter quantity per row</Label>
                <Input value={bulkSearch} onChange={e => setBulkSearch(e.target.value)} placeholder="Search client name / ID" className="h-8 text-xs w-64" data-testid="bulk-client-search" />
              </div>
              <div className="border border-brand-border rounded-lg overflow-hidden max-h-[42vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-brand-bg/95 backdrop-blur">
                    <tr className="border-b border-brand-border">
                      {['Client', 'ID', 'Balance', 'Quantity', 'Row Net P&L'].map(h => (
                        <th key={h} className="text-left py-2 px-3 text-[10px] uppercase tracking-widest font-semibold text-brand-text-secondary">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bulkClients.length === 0 ? (
                      <tr><td colSpan={5} className="py-6 text-center text-xs text-brand-text-secondary">No clients match.</td></tr>
                    ) : bulkClients.map(c => {
                      const q = parseFloat(bulkQty[c.id]) || 0;
                      const rowNet = q * bulkPreview.perUnit;
                      const isEntered = bulkQty[c.id] && q > 0;
                      return (
                        <tr key={c.id} className={`border-b border-brand-border/40 ${isEntered ? 'bg-brand-primary/5' : ''}`} data-testid={`bulk-row-${c.id}`}>
                          <td className="py-1.5 px-3 text-xs font-medium text-brand-text-primary">{c.name}</td>
                          <td className="py-1.5 px-3 text-[10px] font-mono text-brand-text-secondary">{c.client_id}</td>
                          <td className="py-1.5 px-3 text-xs font-mono">${Number(c.current_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                          <td className="py-1.5 px-3 w-32">
                            <Input
                              type="number" step="0.0001" min="0"
                              value={bulkQty[c.id] || ''}
                              onChange={e => setBulkQty({ ...bulkQty, [c.id]: e.target.value })}
                              className="h-8 text-xs font-mono text-right"
                              placeholder="0"
                              data-testid={`bulk-qty-${c.id}`}
                            />
                          </td>
                          <td className={`py-1.5 px-3 text-xs font-mono font-semibold ${rowNet > 0 ? 'text-brand-success' : rowNet < 0 ? 'text-brand-error' : 'text-brand-text-secondary'}`}>
                            {isEntered ? `$${rowNet.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-brand-text-secondary mt-1.5">Leave a row blank to skip that client. Only rows with quantity {'>'} 0 will be saved.</p>
            </div>
          </div>

          <DialogFooter className="p-4 border-t border-brand-border bg-brand-bg/50">
            <Button variant="outline" onClick={() => setBulkOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleBulkSave} disabled={bulkSaving || bulkPreview.rowCount === 0} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm" data-testid="bulk-save-btn">
              {bulkSaving ? <><Loader2 size={14} className="animate-spin mr-2" /> Saving…</> : <><CheckCircle2 size={14} className="mr-2" /> Save {bulkPreview.rowCount} Trade{bulkPreview.rowCount === 1 ? '' : 's'}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
